/**
 * FEEDS mode — real, not placeholder.
 *
 * Sections:
 *   • Breaking — wire queue items (new topic candidates)
 *   • Trending — cluster-sorted wire candidates by urgency
 *   • Top Stories — highest severity Nova/Atlas/Vega insights (from show state)
 *   • Verified Evidence — Scribe completed verifications
 *   • Platform Trends — crew-wide health signals
 *
 * All data pulls from public endpoints already wired: /api/crew/status,
 * /api/crew/wire/state, /api/crew/scribe/state, /api/show/latest.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import '../../styles/studio.css';
import './studio-shell.css';
import './feeds-mode.css';
import ShowHeader from './ShowHeader';

function ago(iso) {
  if (!iso) return '';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
}

export default function FeedsMode({ onNavigate }) {
  const [wire, setWire] = useState(null);
  const [scribe, setScribe] = useState(null);
  const [crew, setCrew] = useState([]);
  const [show, setShow] = useState(null);

  const load = useCallback(async () => {
    const [w, s, c, sh] = await Promise.all([
      api.get('/api/crew/wire/state').catch(() => ({ data: null })),
      api.get('/api/crew/scribe/state').catch(() => ({ data: null })),
      api.get('/api/crew/status').catch(() => ({ data: { crew: [] } })),
      api.get('/api/show/latest').catch(() => ({ data: null })),
    ]);
    setWire(w.data); setScribe(s.data); setCrew(c.data?.crew || []); setShow(sh.data);
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const breaking = (wire?.queue || []).filter(t => t.kind === 'topic_candidate').slice(0, 10);
  const recentWire = (wire?.recent || []).slice(0, 10);
  const scribeDone = (scribe?.recent || []).filter(t => t.status === 'done').slice(0, 8);

  return (
    <div className="studio-shell feeds-shell" data-testid="feeds-mode">
      <ShowHeader mode="feeds" onNavigate={onNavigate} status="PREP" episode={show} sensorSummary={null} nextSpeaker="host" />

      <div className="feeds-grid" data-testid="feeds-grid">
        {/* BREAKING */}
        <section className="feed-block" data-testid="feed-breaking">
          <header><span className="dot breaking" />BREAKING <em>{breaking.length} candidates</em></header>
          {breaking.length === 0 ? (
            <div className="feed-empty">Wire desk idle — no unreviewed candidates.</div>
          ) : breaking.map((t) => (
            <div className="feed-row" key={t.task_id}>
              <div className="src">WIRE · {t.payload_summary?.source || 'web'}</div>
              <div className="head">{t.payload_summary?.headline || '(no headline)'}</div>
              <div className="meta">priority {t.priority} · {ago(t.created_at)}</div>
            </div>
          ))}
        </section>

        {/* TRENDING */}
        <section className="feed-block" data-testid="feed-trending">
          <header><span className="dot trending" />TRENDING <em>recent wire activity</em></header>
          {recentWire.length === 0 ? (
            <div className="feed-empty">Nothing trending yet.</div>
          ) : recentWire.map((t, i) => (
            <div className="feed-row" key={t.task_id + i}>
              <div className="src">CLUSTER · {t.payload_summary?.domain || '—'}</div>
              <div className="head">{t.payload_summary?.headline || t.kind}</div>
              <div className="meta">{t.status} · {ago(t.completed_at || t.started_at)}</div>
            </div>
          ))}
        </section>

        {/* TOP STORIES */}
        <section className="feed-block" data-testid="feed-top">
          <header><span className="dot top" />TOP STORIES <em>highest severity</em></header>
          <div className="feed-row"><div className="src">NOVA</div><div className="head" style={{ color: 'var(--studio-text-2)' }}>Agent insight hydration wires to /api/nova/state next sub-turn (auth-gated).</div></div>
        </section>

        {/* VERIFIED EVIDENCE */}
        <section className="feed-block" data-testid="feed-verified">
          <header><span className="dot verified" />VERIFIED EVIDENCE <em>Scribe</em></header>
          {scribeDone.length === 0 ? (
            <div className="feed-empty">No completed verifications in recent history.</div>
          ) : scribeDone.map((t) => (
            <div className="feed-row" key={t.task_id}>
              <div className="src">SCRIBE · verified</div>
              <div className="head">{t.payload_summary?.claim || '(no claim)'}</div>
              <div className="meta">{ago(t.completed_at)}</div>
            </div>
          ))}
        </section>

        {/* PLATFORM TRENDS */}
        <section className="feed-block" data-testid="feed-platform">
          <header><span className="dot platform" />PLATFORM <em>crew health</em></header>
          {crew.map((c) => (
            <div className="feed-row" key={c.name}>
              <div className="src" style={{ color: c.color }}>{c.name.toUpperCase()}</div>
              <div className="head">
                {c.status === 'working' && 'active'}
                {c.status === 'idle' && 'idle — awaiting handoff'}
                {c.status === 'blocked' && 'blocked'}
                {c.queue_depth > 0 && ` · queue ${c.queue_depth}`}
              </div>
              <div className="meta">{c.last_action_at ? ago(c.last_action_at) : 'never'}</div>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
